import axios from "axios";
import { supabase } from "@/lib/supabase";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BASE}/api`;

export const api = axios.create({ baseURL: API_BASE });

// Attach fresh Supabase access token on every request. If getSession() returns
// no session we abort the request client-side rather than sending an
// un-authenticated call the backend will 401 anyway.
api.interceptors.request.use(async (config) => {
  const { data, error } = await supabase.auth.getSession();
  if (error) {
    if (process.env.NODE_ENV !== "production") console.warn("[api] getSession error:", error);
  }
  const token = data?.session?.access_token;
  if (!token) {
    // Cancel the request — response interceptor will redirect to /login.
    return Promise.reject({
      __no_session: true,
      response: { status: 401, data: { detail: "Session expired. Please sign in again." } },
      config,
    });
  }
  config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// On 401 anywhere: sign out and bounce to /login (once). Also log the real
// server error so debugging in prod isn't blind.
let signingOut = false;
api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;
    if (process.env.NODE_ENV !== "production") {
      // Surface the actual server error message instead of the generic axios one.
      // eslint-disable-next-line no-console
      console.error("[api] request failed:", {
        url: err?.config?.url,
        status,
        detail,
        raw: err?.response?.data,
      });
    }
    if (status === 401 && !signingOut) {
      signingOut = true;
      try { await supabase.auth.signOut(); } catch { /* ignore */ }
      // Redirect after signOut settles. Preserve current path so we can return.
      const back = encodeURIComponent(window.location.pathname + window.location.search);
      // Avoid an infinite loop if we're already on /login
      if (!window.location.pathname.startsWith("/login")) {
        window.location.replace(`/login?next=${back}&reason=session_expired`);
      }
      setTimeout(() => { signingOut = false; }, 2000);
    }
    return Promise.reject(err);
  }
);

// Backwards compat: old code called setToken/getToken to store a JWT in localStorage.
// With Supabase, sessions are managed by supabase-js itself.
export function getToken() { return null; }
export function setToken() { /* no-op — Supabase manages sessions */ }

export function apiError(err) {
  const d = err?.response?.data?.detail ?? err?.message;
  if (!d) return "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(d);
}
